<template>
  <div class="top-five-doughnut-chart d-flex justify-content-between">
    <div
      class="top-five-doughnut-chart__canvas d-flex flex-column justify-content-center i-mx-14"
      @mouseleave="resetOnMouseOut"
      @mouseout="resetOnMouseOut"
    >
      <canvas ref="topFiveChartCanvas"></canvas>
      <div
        class="top-five-doughnut-chart__text text-center d-flex flex-column align-items-center justify-content-center"
      >
        <span class="i-text-body-1">{{ chartTitle }}</span>
        <span class="i-text-heading-2">{{ chartTotalAmount }}</span>
      </div>
      <div
        v-show="showTooltip"
        ref="topFiveChartTooltip"
        class="top-five-doughnut-chart__tooltip i-radius-md i-p-md"
      >
        <p class="i-text-body-3 i-mb-1">
          {{ chartTooltipTitle }}
        </p>
        <p class="i-text-subheading-1 i-m-0">{{ chartTooltipContent }}</p>
      </div>
    </div>
    <div
      id="legend-container"
      class="top-five-doughnut-chart__legend-container"
    >
      <table class="w-100" aria-label="Top 5 sources doughnut chart legend">
        <thead>
          <tr class="i-text-subheading-3">
            <th class="colour-key-th">&nbsp;</th>
            <th>{{ tableHeading }}</th>
            <th
              v-if="isLoanInsights"
              class="text-right d-flex justify-content-end"
            >
              Total Disbursements
              <info-tooltip
                class="i-ml-2"
                title="Disbursement"
                subtitle="Loan disbursements are transfers of funds from a lender to a borrower as part of a loan agreement."
              ></info-tooltip>
            </th>
            <th class="text-right">{{ legendAmountColumnHeading }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in legendItems"
            :id="`legend-${index}`"
            :key="`legend-${index}`"
            class="i-text-subheading-2"
            @mouseover="hoverLegendItem(index)"
            @mouseleave="resetOnMouseOut"
            @mouseout="resetOnMouseOut"
          >
            <td class="colour-key">
              <span
                v-if="hasData"
                class="colour-icon"
                :style="`background-color: ${chartColours[index]}`"
              ></span>
              <span
                v-else
                class="colour-icon"
                :style="`background-color: ${emptyIconColour}`"
              ></span>
            </td>
            <td>
              <div class="d-flex align-items-center">
                <span>{{ item.source }}</span>
                <info-tooltip
                  v-if="item.other"
                  class="i-ml-2"
                  :title="otherTooltipTitle"
                  :subtitle="otherTooltipContent"
                ></info-tooltip>
              </div>
            </td>
            <td v-if="isLoanInsights" class="amount text-right">
              {{ formatLegendAmount(item.disbursement) }}
            </td>
            <td class="amount text-right">
              {{ formatLegendAmount(item.amount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Chart from 'chart.js/auto';
import { toCurrency } from '@/utils/toCurrency';
import componentText from '@/V2/credit/granular-insights/components/components.text';
import palette from '@inscribe/signal/dist/palette';
import {
  externalDoughnutPlugin,
  getTransactionTotalsProperty,
} from '@/V2/credit/granular-insights/components/components.utils';
import { LOAN_INSIGHTS_SLUG } from '@/V2/credit/granular-insights/constants';
import InfoTooltip from '@/shared/components/InfoTooltip';

const emptyTableItem = {
  source: '-',
  disbursement: '-',
  amount: '-',
};

const chartColours = [
  palette['light-primary-110'],
  palette['light-primary-90'],
  palette['light-primary-80'],
  palette['light-primary-70'],
  palette['light-primary-60'],
  palette['light-primary-50'],
];

export default {
  components: {
    InfoTooltip,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    chartData: {
      type: Array,
      default: () => [],
    },
    chartTitle: {
      type: String,
      default: '',
    },
    tableHeading: {
      type: String,
      default: '',
    },
    isCategory: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hasData: false,
      showTooltip: false,
      chartTooltipTitle: '',
      chartTooltipContent: '',
      chartTotalAmount: null,
      formattedData: null,
      otherTotalAmount: null,
      localChartData: null,
      legendItems: [],
      emptyIconColour: palette['light-neutral-60'],
      toCurrency,
      chartColours,
    };
  },
  computed: {
    ...mapState({
      credit: 'credit',
    }),

    ...mapGetters(['insightsTotals']),

    chartOptions() {
      return {
        borderWidth: 1,
        cutout: '80%',
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
          },
          emptyDoughnut: {
            color: palette['light-primary-40'],
            width: 30,
            radiusDecrease: 15,
          },
        },
        onHover: this.onChartHover,
      };
    },

    isLoanInsights() {
      return this.type === LOAN_INSIGHTS_SLUG;
    },

    legendAmountColumnHeading() {
      return componentText[this.type].topFiveSources.legendAmountColumn;
    },

    otherTooltipTitle() {
      return this.isCategory
        ? componentText[this.type].topFiveSources.otherCategoryTooltipTitle
        : componentText[this.type].topFiveSources.otherTooltipTitle;
    },

    otherTooltipContent() {
      return this.isCategory
        ? componentText[this.type].topFiveSources.otherCategoryTooltipContent
        : componentText[this.type].topFiveSources.otherTooltipContent;
    },
  },
  watch: {
    chartData: {
      handler(newValue, oldValue) {
        if (newValue) {
          this.updateChart();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.createChart();
  },
  methods: {
    createChart() {
      const chartCanvas = this.$refs.topFiveChartCanvas;

      this.chart = new Chart(chartCanvas, {
        type: 'doughnut',
        data: {
          datasets: this.chartDataset(),
        },
        options: this.chartOptions,
        plugins: [externalDoughnutPlugin],
      });
    },

    onChartHover(event, chartElement) {
      if (chartElement.length > 0) {
        const activeIndex = chartElement[0].index;

        if (this.hasData) {
          this.setTooltipContent(activeIndex);

          this.removeActiveClassFromElements();

          this.addActiveClassToElement(activeIndex);

          this.positionTooltip(event, chartElement[0].element);

          this.chart.update();
        }
      }
    },

    fillEmptyTable() {
      const emptyTable = [];
      for (let i = 0; i < 5; i++) {
        emptyTable.push(emptyTableItem);
      }
      return emptyTable;
    },

    hoverLegendItem(index) {
      if (this.hasData) {
        this.setTooltipContent(index);
        this.positionTooltip(event, this.chart._metasets[0].data[index]);
      }
    },

    setTooltipContent(activeIndex) {
      this.chartTooltipTitle =
        this.chart.data.datasets[0].data[activeIndex].label;
      this.chartTooltipContent = `${toCurrency(
        this.chart.data.datasets[0].data[activeIndex].value,
      )}`;
      this.showTooltip = true;
    },

    positionTooltip(event, activeElement) {
      const tooltip = this.$refs.topFiveChartTooltip;

      const radius = Math.min(
        activeElement.innerRadius + activeElement.outerRadius,
        activeElement.outerRadius,
      );

      // Calculate the angle of the active segment
      const startAngle = activeElement.startAngle;
      const endAngle = activeElement.endAngle;
      const angle = (startAngle + endAngle) / 2;

      // Calculate the x and y coordinates of the tooltip
      const x = activeElement.x + Math.cos(angle) * radius;
      const y = activeElement.y + Math.sin(angle) * radius;

      tooltip.style.top = y + 'px';
      tooltip.style.left = x + 'px';

      if (x < 150) {
        tooltip.classList.add('transform-x');
      } else {
        tooltip.classList.remove('transform-x');
      }

      if (y < 150) {
        tooltip.classList.add('transform-y');
      } else {
        tooltip.classList.remove('transform-y');
      }

      if (x < 150 && y < 150) {
        tooltip.classList.add('transform-xy');
      } else {
        tooltip.classList.remove('transform-xy');
      }
    },

    setChartAmountValues(transactions) {
      const insightTotal =
        this.insightsTotals[getTransactionTotalsProperty(this.type)];

      if (transactions.length === 0) {
        this.chartTotalAmount = toCurrency(insightTotal);

        this.otherTotalAmount = insightTotal;
      } else {
        const sumDataTotal = transactions.reduce(
          (total, item) => total + item.amount,
          0,
        );
        this.chartTotalAmount = toCurrency(insightTotal);

        this.otherTotalAmount = insightTotal - sumDataTotal;
      }
    },

    formatLegendAmount(amount) {
      if (amount === 0) {
        return '-';
      }
      return toCurrency(amount);
    },

    chartDataset() {
      if (
        this.chartData.length === 0 &&
        !this.insightsTotals[getTransactionTotalsProperty(this.type)]
      ) {
        this.hasData = false;
        this.chartTotalAmount = '$-';
        this.legendItems = [...this.fillEmptyTable()];

        return [
          {
            data: [],
          },
        ];
      } else {
        this.hasData = true;
        this.setChartAmountValues(this.chartData);
        const otherObject = [];
        if (this.otherTotalAmount > 0) {
          const otherItem = {
            source: this.otherTooltipTitle,
            amount: this.otherTotalAmount,
            other: true,
          };

          if (this.isLoanInsights) {
            otherItem.disbursement = this.insightsTotals.credits_total;
          }

          otherObject.push(otherItem);
        }
        this.localChartData = [...this.chartData, ...otherObject];
        this.formattedData = this.dataFormatMapper(this.localChartData);
        this.legendItems = this.localChartData;

        return [
          {
            data: [...this.formattedData],
            backgroundColor: [...chartColours],
          },
        ];
      }
    },

    updateChart() {
      this.chart.data.datasets = this.chartDataset();
      this.chart.update();
    },

    resetOnMouseOut() {
      // Hide the tooltip when mouse leaves the chart area
      if (this.showTooltip) {
        this.showTooltip = false;
        this.chartTooltipContent = '';
        this.chartTooltipTitle = '';
      }
      this.removeActiveClassFromElements();
    },

    dataFormatMapper(transactions) {
      return transactions.map((transaction) => ({
        label: transaction.source,
        value: transaction.amount,
      }));
    },

    removeActiveClassFromElements() {
      const elements = document.querySelectorAll('[id^="legend-"]');
      elements.forEach((element) => {
        element.classList.remove('active');
      });
    },

    addActiveClassToElement(activeIndex) {
      document.getElementById(`legend-${activeIndex}`).classList.add('active');
    },
  },
};
</script>

<style lang="scss" scoped>
$border-radius: 3px;

.top-five-doughnut-chart {
  @include respond-to('large') {
    flex-wrap: wrap;
    justify-content: center !important;
  }

  &__canvas {
    width: 300px;
    height: 300px;
    position: relative;

    @include respond-to('large') {
      margin-bottom: map-get($increments, 10) !important;
    }
  }

  &__text {
    z-index: 0;
    top: 40px;
    position: absolute;
    height: 220px;
    width: 220px;
    word-break: break-all;
    border-radius: 50%;
    left: 40px;
  }

  &__tooltip {
    position: absolute;
    min-width: 140px;
    text-align: center;
    background-color: $light-neutral-default;
    border: 1px solid $light-neutral-60;
    box-shadow: 0px 1px 20px 0px rgba($light-neutral-110, 0.15);
    z-index: 999;
    &.transform-y {
      transform: translateY(-100%);
    }
    &.transform-x {
      transform: translateX(-100%);
    }
    &.transform-xy {
      transform: translate(-100%, -100%);
    }
  }

  &__legend-container {
    min-width: 580px;
    @include respond-to('large') {
      min-width: 100%;
    }

    table {
      color: $light-neutral-120;

      thead {
        th {
          color: $light-neutral-110;
          padding: 12px;

          &:nth-child(2) {
            padding-left: 0;
          }
          &.colour-key-th {
            width: 40px;
            padding-right: 24px;
          }
        }
      }

      tbody {
        tr {
          &.active,
          &:hover {
            background-color: $light-neutral-20 !important;
            transition: 0.4s ease-in-out;
            border-radius: $border-radius;
          }

          td {
            padding: 16px 12px;
            border-top: 1px solid $light-neutral-50 !important;

            &:nth-child(2) {
              padding-left: 0;
            }

            &.colour-key {
              border-top: none !important;
              width: 40px;
              padding-right: 24px;
              text-align: center;
              .colour-icon {
                width: 16px;
                height: 16px;
                border-radius: $border-radius;
                display: inline-block;
              }
            }

            &.amount {
              width: 29%;
            }
          }
        }
      }
    }
  }
}
</style>
